import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Modal,
  Pressable,
  TouchableOpacity,
  ScrollView,
  Platform,
  TextInput,
  ActivityIndicator,
  useWindowDimensions,
} from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { AdminColors } from '../Admin/constants/adminColors';
import FontFamily from '../../constants/fonts';
import { QueueItem } from './types';
import { LeaderOption } from './SetCoverageModal';

export const REASSIGN_REASONS = [
  'Planned absence',
  'Shift coverage',
  'Team change',
  'Other',
];

export interface ReassignTaskSubmitData {
  taskId: string;
  reassignTo: string;
  reassignToName: string;
  reason: string;
  reasonOther?: string;
  isDirectReassign: boolean;
}

interface ReassignTaskModalProps {
  visible: boolean;
  onClose: () => void;
  onSubmit: (data: ReassignTaskSubmitData) => void | Promise<void>;
  item: QueueItem | null;
  leaders: LeaderOption[];
  userRoleName?: string;
  userRoleId?: number;
  currentUserEmail?: string;
  isSubmitting?: boolean;
}

/** Searchable Leader Dropdown attached directly beneath the search input */
interface AttachedSearchableDropdownProps {
  label: string;
  placeholder?: string;
  options: LeaderOption[];
  selectedValue: LeaderOption | null;
  onSelect: (option: LeaderOption) => void;
  disabled?: boolean;
}

const AttachedSearchableDropdown: React.FC<AttachedSearchableDropdownProps> = ({
  label,
  placeholder = 'Select a leader',
  options,
  selectedValue,
  onSelect,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const containerRef = useRef<any>(null);

  const filteredOptions = useMemo(() => {
    if (!searchText.trim()) return options;
    const q = searchText.toLowerCase().trim();
    return options.filter((o) => {
      const display = (o.displayText || o.name || '').toLowerCase();
      return display.includes(q);
    });
  }, [options, searchText]);

  useEffect(() => {
    if (!isOpen) {
      setSearchText('');
    }
  }, [isOpen]);

  // Click outside listener for web
  useEffect(() => {
    if (Platform.OS === 'web' && isOpen) {
      const handleClickOutside = (e: any) => {
        const domNode = containerRef.current;
        if (domNode && typeof domNode.contains === 'function') {
          if (domNode.contains(e.target)) {
            return;
          }
        }
        setIsOpen(false);
        setIsFocused(false);
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const displayLabel = useMemo(() => {
    if (!selectedValue) return '';
    return (
      selectedValue.displayText ||
      (selectedValue.shift ? `${selectedValue.name} (${selectedValue.shift} shift)` : selectedValue.name) ||
      ''
    );
  }, [selectedValue]);

  const handleSelectOption = (opt: LeaderOption) => {
    onSelect(opt);
    setSearchText('');
    setIsOpen(false);
    setIsFocused(false);
  };

  return (
    <View
      ref={containerRef}
      style={[styles.dropdownContainer, { zIndex: isOpen ? 9999 : 10 }]}
    >
      <Text style={styles.fieldLabel}>{label}</Text>

      {/* Search Input / Trigger Box */}
      <View
        style={[
          styles.triggerBox,
          (isOpen || isFocused) && styles.triggerBoxOpen,
          disabled && styles.triggerBoxDisabled,
        ]}
      >
        <TextInput
          style={[
            styles.searchInputField,
            Platform.OS === 'web'
              ? ({
                  outline: 'none',
                  outlineWidth: 0,
                  outlineStyle: 'none',
                  boxShadow: 'none',
                } as any)
              : null,
          ]}
          placeholder={isOpen ? (displayLabel ? `Filter (${displayLabel})` : 'Search leader...') : displayLabel || placeholder}
          placeholderTextColor={!isOpen && displayLabel ? AdminColors.textPrimary : AdminColors.textMuted}
          value={isOpen ? searchText : displayLabel}
          onChangeText={(txt) => {
            setSearchText(txt);
            if (!isOpen) setIsOpen(true);
          }}
          onFocus={() => {
            if (!disabled) {
              setIsFocused(true);
              setIsOpen(true);
            }
          }}
          onBlur={() => {
            setIsFocused(false);
          }}
          editable={!disabled}
        />

        <View style={styles.triggerIcons}>
          {isOpen && searchText.length > 0 && (
            <TouchableOpacity
              onPress={() => setSearchText('')}
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              style={styles.clearIcon}
            >
              <MaterialIcons name="close" size={16} color={AdminColors.textSecondary} />
            </TouchableOpacity>
          )}
          <TouchableOpacity
            onPress={() => {
              if (!disabled) {
                setIsOpen((prev) => !prev);
              }
            }}
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          >
            <MaterialIcons
              name={isOpen ? 'arrow-drop-up' : 'arrow-drop-down'}
              size={24}
              color={AdminColors.textSecondary}
            />
          </TouchableOpacity>
        </View>
      </View>

      {/* Attached Dropdown Menu List */}
      {isOpen && (
        <View style={styles.attachedMenu}>
          <ScrollView
            style={styles.attachedMenuList}
            nestedScrollEnabled
            keyboardShouldPersistTaps="handled"
          >
            {filteredOptions.length === 0 ? (
              <View style={styles.emptyOptionItem}>
                <Text style={styles.emptyOptionText}>No leaders found</Text>
              </View>
            ) : (
              filteredOptions.map((opt) => {
                const isSelected = Boolean(
                  selectedValue &&
                  (String(selectedValue.id) === String(opt.id) ||
                    (selectedValue.name && opt.name && selectedValue.name.toLowerCase() === opt.name.toLowerCase()))
                );
                const isHovered = hoveredId === opt.id;
                const optText = opt.displayText || (opt.shift ? `${opt.name} (${opt.shift} shift)` : opt.name);
                return (
                  <TouchableOpacity
                    key={opt.id}
                    style={[
                      styles.optionRow,
                      isSelected && styles.optionRowSelected,
                      isHovered && styles.optionRowHovered,
                    ]}
                    onPress={() => handleSelectOption(opt)}
                    {...(Platform.OS === 'web'
                      ? {
                          onMouseDown: (e: any) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleSelectOption(opt);
                          },
                          onMouseEnter: () => setHoveredId(opt.id),
                          onMouseLeave: () => setHoveredId(null),
                        }
                      : {})}
                  >
                    <Text
                      style={[
                        styles.optionText,
                        isSelected && styles.optionTextSelected,
                      ]}
                      numberOfLines={1}
                    >
                      {optText}
                    </Text>
                    {isSelected && (
                      <MaterialIcons name="check" size={18} color={AdminColors.blue700} />
                    )}
                  </TouchableOpacity>
                );
              })
            )}
          </ScrollView>
        </View>
      )}
    </View>
  );
};

export const ReassignTaskModal: React.FC<ReassignTaskModalProps> = ({
  visible,
  onClose,
  onSubmit,
  item,
  leaders,
  userRoleName,
  userRoleId,
  currentUserEmail,
  isSubmitting = false,
}) => {
  const { height: windowHeight } = useWindowDimensions();

  const [selectedLeader, setSelectedLeader] = useState<LeaderOption | null>(null);
  const [reason, setReason] = useState<string>('');
  const [reasonOther, setReasonOther] = useState<string>('');
  const [isReasonOpen, setIsReasonOpen] = useState<boolean>(false);
  const reasonContainerRef = useRef<any>(null);

  const prevVisibleRef = useRef(false);

  // Filter out the current assignee from available leader options
  const eligibleLeaders = useMemo(() => {
    const currentAssignee = (item?.assignedTo ?? '').toLowerCase().trim();
    const currentAssigneeEmail = (item?.assigneeEmail ?? '').toLowerCase().trim();
    const loggedInEmail = (currentUserEmail ?? '').toLowerCase().trim();

    return leaders.filter((l) => {
      const leaderName = (l.name ?? '').toLowerCase().trim();
      const leaderId = (l.id ?? '').toLowerCase().trim();
      if (currentAssignee && (leaderName === currentAssignee || leaderId === currentAssignee)) return false;
      if (currentAssigneeEmail && leaderId === currentAssigneeEmail) return false;
      if (loggedInEmail && leaderId === loggedInEmail) return false;
      return true;
    });
  }, [leaders, item, currentUserEmail]);

  // Reset form when modal opens
  useEffect(() => {
    if (visible && !prevVisibleRef.current) {
      setSelectedLeader(null);
      setReason('');
      setReasonOther('');
      setIsReasonOpen(false);
    }
    prevVisibleRef.current = visible;
  }, [visible]);

  // Click outside listener for reason dropdown
  useEffect(() => {
    if (Platform.OS === 'web' && isReasonOpen) {
      const handleClickOutside = (e: any) => {
        const domNode = reasonContainerRef.current;
        if (domNode && typeof domNode.contains === 'function') {
          if (domNode.contains(e.target)) return;
        }
        setIsReasonOpen(false);
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isReasonOpen]);

  // Role checking: Site Supervisor (8) & Team Lead (9) offer tasks; Site Leader (4)+ reassign directly
  const isSupervisorOrTeamLead =
    userRoleId === 8 ||
    userRoleId === 9 ||
    userRoleName === 'Site Supervisor' ||
    userRoleName === 'Team Lead';

  const isDirectReassign = !isSupervisorOrTeamLead;

  // ── Guardrails ─────────────────────────────────────────────────────────────
  // 1. Max 2 reassignments limit
  const reassignCount = item?.reassignCount ?? 0;
  const isExceededMaxReassign = reassignCount >= 2;

  // 2. Due date cutoff: Site Supervisor / Team Lead cannot reassign tasks due tomorrow or overdue
  const isDueTomorrowOrOverdue =
    isSupervisorOrTeamLead &&
    item?.daysUntilDue != null &&
    item.daysUntilDue <= 1;

  // 3. Bounce back: cannot offer a task back to the leader who just offered it
  const isBounceBack =
    Boolean(
      selectedLeader &&
      item?.originalAssignee &&
      (String(selectedLeader.id).toLowerCase() === String(item.originalAssignee).toLowerCase() ||
        selectedLeader.name.toLowerCase() === (item.originalAssigneeName ?? '').toLowerCase())
    );

  const isOtherReason = reason === 'Other';
  const isOtherTextValid = !isOtherReason || (reasonOther.trim().length > 0 && reasonOther.length <= 100);

  const isFormValid =
    Boolean(selectedLeader && reason && isOtherTextValid) &&
    !isExceededMaxReassign &&
    !isDueTomorrowOrOverdue &&
    !isBounceBack &&
    !isSubmitting;

  const handleSubmit = async () => {
    if (!isFormValid || !item || !selectedLeader) return;
    const leaderName = selectedLeader.name ?? selectedLeader.displayText ?? selectedLeader.id;

    await onSubmit({
      taskId: item.id,
      reassignTo: selectedLeader.id,
      reassignToName: leaderName,
      reason,
      reasonOther: isOtherReason ? reasonOther.trim() : undefined,
      isDirectReassign,
    });
  };

  const confirmButtonLabel = isSubmitting
    ? 'Saving…'
    : isDirectReassign
      ? 'Reassign'
      : 'Offer task';

  const title = item
    ? `Reassign ${item.type} for ${item.employeeName}`
    : 'Reassign Task';

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={styles.scrim}>
        <Pressable style={StyleSheet.absoluteFill} onPress={onClose} accessibilityLabel="Close dialog" />
        <View style={[styles.modal, { maxHeight: windowHeight * 0.9 }]}>
          {/* Header */}
          <View style={styles.header}>
            <Text style={styles.title} numberOfLines={1}>{title}</Text>
            <TouchableOpacity onPress={onClose} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }} accessibilityLabel="Close">
              <MaterialIcons name="close" size={20} color={AdminColors.textSecondary} />
            </TouchableOpacity>
          </View>

          {/* Body */}
          <View style={styles.body}>
            {/* Guardrail Warning Banners */}
            {isExceededMaxReassign && (
              <View style={styles.warningBanner}>
                <MaterialIcons name="error-outline" size={16} color={AdminColors.tagRed.text} />
                <Text style={styles.warningBannerText}>
                  This task has already been reassigned twice. Complete it, or ask a Site Leader to move it.
                </Text>
              </View>
            )}

            {isDueTomorrowOrOverdue && (
              <View style={styles.warningBanner}>
                <MaterialIcons name="error-outline" size={16} color={AdminColors.tagRed.text} />
                <Text style={styles.warningBannerText}>
                  This task is due tomorrow. Complete it or ask a Site Leader to reassign it.
                </Text>
              </View>
            )}

            {isBounceBack && (
              <View style={styles.warningBanner}>
                <MaterialIcons name="error-outline" size={16} color={AdminColors.tagRed.text} />
                <Text style={styles.warningBannerText}>
                  You cannot offer a task back to the leader who just offered it to you.
                </Text>
              </View>
            )}

            {/* Task Info Summary */}
            <View style={styles.taskInfoBox}>
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>Current assignee:</Text>
                <Text style={styles.infoValue}>{item?.assignedTo ?? '—'}</Text>
              </View>
              {item?.dueDate && (
                <View style={styles.infoRow}>
                  <Text style={styles.infoLabel}>Due date:</Text>
                  <Text style={styles.infoValue}>{item.dueDate}</Text>
                </View>
              )}
            </View>

            {/* Reassign to: Searchable Attached Dropdown */}
            <AttachedSearchableDropdown
              label="Reassign to"
              placeholder={eligibleLeaders.length === 0 ? 'No other leader is available at this location.' : 'Select a leader'}
              options={eligibleLeaders}
              selectedValue={selectedLeader}
              onSelect={(leader) => setSelectedLeader(leader)}
              disabled={eligibleLeaders.length === 0 || isExceededMaxReassign || isDueTomorrowOrOverdue}
            />

            {/* Reason Dropdown */}
            <View
              ref={reasonContainerRef}
              style={[styles.dropdownContainer, { zIndex: isReasonOpen ? 9998 : 5 }]}
            >
              <Text style={styles.fieldLabel}>Reason</Text>
              <TouchableOpacity
                style={[
                  styles.triggerBox,
                  isReasonOpen && styles.triggerBoxOpen,
                  (isExceededMaxReassign || isDueTomorrowOrOverdue) && styles.triggerBoxDisabled,
                ]}
                onPress={() => {
                  if (!isExceededMaxReassign && !isDueTomorrowOrOverdue) {
                    setIsReasonOpen((prev) => !prev);
                  }
                }}
                disabled={isExceededMaxReassign || isDueTomorrowOrOverdue}
              >
                <Text
                  style={[
                    styles.reasonSelectedText,
                    !reason && styles.reasonPlaceholderText,
                  ]}
                >
                  {reason || 'Select a reason'}
                </Text>
                <MaterialIcons
                  name={isReasonOpen ? 'arrow-drop-up' : 'arrow-drop-down'}
                  size={24}
                  color={AdminColors.textSecondary}
                />
              </TouchableOpacity>

              {isReasonOpen && (
                <View style={styles.attachedMenu}>
                  {REASSIGN_REASONS.map((r) => {
                    const isSelected = reason === r;
                    return (
                      <TouchableOpacity
                        key={r}
                        style={[styles.optionRow, isSelected && styles.optionRowSelected]}
                        onPress={() => {
                          setReason(r);
                          setIsReasonOpen(false);
                        }}
                        {...(Platform.OS === 'web'
                          ? {
                              onMouseDown: (e: any) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setReason(r);
                                setIsReasonOpen(false);
                              },
                            }
                          : {})}
                      >
                        <Text style={[styles.optionText, isSelected && styles.optionTextSelected]}>
                          {r}
                        </Text>
                        {isSelected && (
                          <MaterialIcons name="check" size={18} color={AdminColors.blue700} />
                        )}
                      </TouchableOpacity>
                    );
                  })}
                </View>
              )}
            </View>

            {/* Other Reason Text Input with 100 character counter */}
            {isOtherReason && (
              <View style={styles.field}>
                <View style={styles.labelWithCounter}>
                  <Text style={styles.fieldLabel}>Specify other reason</Text>
                  <Text style={styles.charCounter}>{reasonOther.length} of 100</Text>
                </View>
                <TextInput
                  style={[
                    styles.textInput,
                    Platform.OS === 'web'
                      ? ({
                          outline: 'none',
                          outlineWidth: 0,
                          outlineStyle: 'none',
                          boxShadow: 'none',
                        } as any)
                      : null,
                  ]}
                  value={reasonOther}
                  onChangeText={(txt) => setReasonOther(txt.slice(0, 100))}
                  placeholder="Enter reason..."
                  placeholderTextColor={AdminColors.textMuted}
                  maxLength={100}
                />
              </View>
            )}

            {/* Disclaimer note */}
            <Text style={styles.noteText}>
              The employee's manager is not changed and the due date does not move.
            </Text>
          </View>

          {/* Footer */}
          <View style={styles.footer}>
            <TouchableOpacity style={styles.cancelBtn} onPress={onClose} disabled={isSubmitting}>
              <Text style={styles.cancelBtnText}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.submitBtn, (!isFormValid || isSubmitting) && styles.submitBtnDisabled]}
              onPress={handleSubmit}
              disabled={!isFormValid || isSubmitting}
            >
              {isSubmitting ? (
                <ActivityIndicator size="small" color={AdminColors.textOnDark} />
              ) : (
                <Text style={styles.submitBtnText}>{confirmButtonLabel}</Text>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  scrim: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  modal: {
    width: '100%',
    maxWidth: 540,
    backgroundColor: AdminColors.bgSurface,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    overflow: 'visible',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 16,
    elevation: 8,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: AdminColors.borderDefault,
  },
  title: {
    fontFamily: FontFamily.bold,
    fontSize: 16,
    color: AdminColors.textPrimary,
    flex: 1,
    marginRight: 10,
  },
  body: {
    padding: 20,
    gap: 16,
    zIndex: 10,
    overflow: 'visible',
  },
  warningBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    padding: 10,
    backgroundColor: AdminColors.tagRed.bg,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: AdminColors.red400,
  },
  warningBannerText: {
    fontFamily: FontFamily.regular,
    fontSize: 12,
    color: AdminColors.tagRed.text,
    flex: 1,
    lineHeight: 16,
  },
  taskInfoBox: {
    backgroundColor: AdminColors.bgPage,
    padding: 12,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    gap: 6,
  },
  infoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  infoLabel: {
    fontFamily: FontFamily.medium,
    fontSize: 12,
    color: AdminColors.textSecondary,
  },
  infoValue: {
    fontFamily: FontFamily.bold,
    fontSize: 12,
    color: AdminColors.textPrimary,
  },
  field: {
    gap: 6,
  },
  fieldLabel: {
    fontFamily: FontFamily.medium,
    fontSize: 12,
    color: AdminColors.textSecondary,
  },
  labelWithCounter: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  charCounter: {
    fontFamily: FontFamily.regular,
    fontSize: 11,
    color: AdminColors.textMuted,
  },
  textInput: {
    height: 38,
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    borderRadius: 6,
    paddingHorizontal: 10,
    fontSize: 13,
    color: AdminColors.textPrimary,
    backgroundColor: AdminColors.bgSurface,
    fontFamily: FontFamily.regular,
  },
  dropdownContainer: {
    position: 'relative',
    gap: 6,
  },
  triggerBox: {
    height: 38,
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    borderRadius: 6,
    backgroundColor: AdminColors.bgSurface,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingLeft: 10,
    paddingRight: 6,
  },
  triggerBoxOpen: {
    borderColor: AdminColors.blue700,
  },
  triggerBoxDisabled: {
    backgroundColor: AdminColors.gray50,
  },
  searchInputField: {
    flex: 1,
    height: '100%',
    fontSize: 13,
    fontFamily: FontFamily.regular,
    color: AdminColors.textPrimary,
    padding: 0,
    margin: 0,
    borderWidth: 0,
    backgroundColor: 'transparent',
  },
  triggerIcons: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  clearIcon: {
    padding: 2,
  },
  reasonSelectedText: {
    fontFamily: FontFamily.regular,
    fontSize: 13,
    color: AdminColors.textPrimary,
  },
  reasonPlaceholderText: {
    color: AdminColors.textMuted,
  },
  attachedMenu: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    marginTop: 4,
    backgroundColor: AdminColors.bgSurface,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    maxHeight: 190,
    zIndex: 99999,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.18,
    shadowRadius: 12,
    elevation: 20,
  },
  attachedMenuList: {
    maxHeight: 188,
  },
  optionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: AdminColors.borderDefault,
  },
  optionRowSelected: {
    backgroundColor: AdminColors.blue50,
  },
  optionRowHovered: {
    backgroundColor: AdminColors.gray50,
  },
  optionText: {
    fontFamily: FontFamily.regular,
    fontSize: 13,
    color: AdminColors.textPrimary,
    flex: 1,
  },
  optionTextSelected: {
    fontFamily: FontFamily.medium,
    color: AdminColors.blue700,
  },
  emptyOptionItem: {
    padding: 14,
    alignItems: 'center',
  },
  emptyOptionText: {
    fontFamily: FontFamily.regular,
    fontSize: 13,
    color: AdminColors.textMuted,
  },
  noteText: {
    fontFamily: FontFamily.regular,
    fontSize: 12,
    color: AdminColors.textMuted,
    lineHeight: 16,
  },
  footer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 10,
    paddingHorizontal: 20,
    paddingVertical: 14,
    borderTopWidth: 1,
    borderTopColor: AdminColors.borderDefault,
    backgroundColor: AdminColors.bgSurface,
    zIndex: 1,
  },
  cancelBtn: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    backgroundColor: AdminColors.bgSurface,
  },
  cancelBtnText: {
    fontFamily: FontFamily.medium,
    fontSize: 13,
    color: AdminColors.textPrimary,
  },
  submitBtn: {
    paddingHorizontal: 18,
    paddingVertical: 8,
    borderRadius: 6,
    backgroundColor: AdminColors.blue700,
    minWidth: 110,
    alignItems: 'center',
    justifyContent: 'center',
  },
  submitBtnDisabled: {
    opacity: 0.5,
  },
  submitBtnText: {
    fontFamily: FontFamily.medium,
    fontSize: 13,
    color: AdminColors.textOnDark,
  },
});
